/**
 * Taxa da plataforma (Lizta):
 * - Lizta: 10% sobre o preço (independente do método)
 * - Cartão: +5% sempre pagos pelo usuário (não saem do time)
 *
 * Taxas do integrador (estimativas): PIX ~0,6% | Cartão ~3,45%.
 *
 * Quando passFeesToUser = true:
 *   - PIX: usuário paga preço + 10% (Lizta).
 *   - Cartão: usuário paga preço + 10% (Lizta) + 5% (cartão).
 *
 * Quando passFeesToUser = false:
 *   - PIX: usuário paga apenas o preço; time recebe ~90% do preço.
 *   - Cartão: usuário paga preço + 5%; time também recebe ~90% do preço (só os 10% saem do time).
 *
 * Aqui usamos aproximações para fins de exibição no frontend.
 */
const PLATFORM_FEE_RATE_PIX = 0.1;
const PLATFORM_FEE_RATE_CARD = 0.15;
const MP_FEE_RATE_PIX = 0.006;
const MP_FEE_RATE_CARD = 0.0345;

/** Taxa plataforma: PIX 10%, Cartão 15% (sobre o preço). */
export function getPlatformFee(pricePerPlayer: number, method: "PIX" | "CARD"): number {
  if (pricePerPlayer <= 0) return 0;
  const rate = method === "PIX" ? PLATFORM_FEE_RATE_PIX : PLATFORM_FEE_RATE_CARD;
  return Math.round(pricePerPlayer * rate * 100) / 100;
}

/**
 * Deriva taxa plataforma do valor cobrado (charged = preço + taxas).
 */
export function getPlatformFeeFromCharged(charged: number, method: "PIX" | "CARD"): number {
  if (charged <= 0) return 0;
  const rate = method === "PIX" ? PLATFORM_FEE_RATE_PIX : PLATFORM_FEE_RATE_CARD;
  return Math.round((charged * rate) / (1 + rate) * 100) / 100;
}

/**
 * Líquido plataforma: taxa plataforma - taxa do integrador.
 */
export function getPlatformNetFromPayment(
  charged: number,
  netFromMp: number,
  method: "PIX" | "CARD"
): number {
  if (charged <= 0) return 0;
  const platformFee = getPlatformFeeFromCharged(charged, method);
  const mpFee = charged - netFromMp;
  return Math.max(0, Math.round((platformFee - mpFee) * 100) / 100);
}

/**
 * Líquido plataforma (Lizta): o que a plataforma recebe (netFromMp) menos o que paga ao time.
 * Usa netAmount real quando disponível; senão estima netFromMp = charged * (1 - mpRate).
 */
export function getPlatformNetFromTransaction(
  charged: number,
  netAmount: number | null,
  pricePerPlayer: number,
  method: "PIX" | "CARD"
): number {
  if (charged <= 0) return 0;
  const mpRate = method === "PIX" ? MP_FEE_RATE_PIX : MP_FEE_RATE_CARD;
  const netFromMp =
    netAmount != null ? netAmount : Math.round(charged * (1 - mpRate) * 100) / 100;
  return Math.max(0, Math.round((netFromMp - pricePerPlayer) * 100) / 100);
}

/**
 * Valor cobrado ao usuário.
 *
 * - Sempre adiciona 5% para cartão (taxa de cartão é sempre do usuário).
 * - A taxa Lizta de 10% só é adicionada ao que o usuário paga quando passFeesToUser = true.
 */
export function getChargedAmount(
  pricePerPlayer: number,
  method: "PIX" | "CARD",
  passFeesToUser: boolean = true,
): number {
  if (pricePerPlayer <= 0) return 0;
  let amount = pricePerPlayer;

  // Lizta (10%) pode ou não ser repassada para o cliente
  if (passFeesToUser) {
    // Usamos sempre a taxa PIX (10%) como base da Lizta, independente do método
    const liztaFee = getPlatformFee(pricePerPlayer, "PIX");
    amount += liztaFee;
  }

  // Taxa de cartão (5%) é sempre paga pelo usuário
  if (method === "CARD") {
    amount = amount * 1.05;
  }

  return Math.round(amount * 100) / 100;
}

/**
 * Estimativa simplificada do quanto o time recebe por usuário (média entre PIX/CARD).
 */
export function estimateReceiptPerUser(
  pricePerPlayer: number,
  passFeesToUser: boolean = true,
): number {
  if (pricePerPlayer <= 0) return 0;
  if (passFeesToUser) {
    return pricePerPlayer;
  }
  // Time sempre perde ~10% (Lizta), independente do método
  const teamRate = 1 - PLATFORM_FEE_RATE_PIX; // 0.9
  return Math.round(pricePerPlayer * teamRate * 100) / 100;
}

export function estimateReceiptPerUserByMethod(
  pricePerPlayer: number,
  _method: "PIX" | "CARD",
  passFeesToUser: boolean = true,
): number {
  if (pricePerPlayer <= 0) return 0;
  if (passFeesToUser) {
    return pricePerPlayer;
  }
  // Time perde apenas os 10% da Lizta, tanto no PIX quanto no cartão
  const teamRate = 1 - PLATFORM_FEE_RATE_PIX; // 0.9
  return Math.round(pricePerPlayer * teamRate * 100) / 100;
}

/**
 * Mensagens amigáveis para códigos de rejeição do Mercado Pago (status_detail).
 * Baseado em: https://www.mercadopago.com.ar/developers/en/docs/checkout-api/how-tos/reasons-for-rejection
 */
const MP_REJECTION_MESSAGES: Record<string, string> = {
  cc_rejected_high_risk:
    "Pagamento não aprovado por segurança. Tente usar outro cartão ou PIX.",
  cc_rejected_call_for_authorize:
    "Seu banco pediu autorização. Entre em contato ou tente outro cartão.",
  cc_rejected_card_disabled:
    "Cartão desabilitado. Tente outro cartão ou PIX.",
  cc_rejected_insufficient_amount:
    "Saldo ou limite insuficiente. Tente outro cartão ou PIX.",
  cc_rejected_blacklist:
    "Pagamento não aprovado. Tente outro cartão ou PIX.",
  cc_rejected_max_attempts:
    "Muitas tentativas. Tente novamente em alguns minutos ou use PIX.",
  cc_rejected_other_reason:
    "Cartão não aprovado. Tente outro cartão ou PIX.",
  cc_amount_rate_limit_exceeded:
    "Limite excedido. Tente em outra hora ou use PIX.",
  cc_rejected_bad_filled_card_number:
    "Número do cartão inválido. Verifique e tente novamente.",
  cc_rejected_bad_filled_date:
    "Data de validade inválida. Verifique e tente novamente.",
  cc_rejected_bad_filled_security_code:
    "Código de segurança (CVV) inválido. Verifique e tente novamente.",
  cc_rejected_bad_filled_other:
    "Dados do cartão incorretos. Verifique e tente novamente.",
};

export function getRejectionMessage(statusDetail: string | null | undefined): string {
  if (!statusDetail) return "Pagamento rejeitado. Tente novamente ou use PIX.";
  const key = String(statusDetail).toLowerCase().trim();
  return MP_REJECTION_MESSAGES[key] ?? `Pagamento rejeitado: ${statusDetail}. Tente outro cartão ou PIX.`;
}

/**
 * Valida um CPF (com dígitos verificadores). Aceita string com ou sem máscara.
 * O CPF é obrigatório no checkout com cartão para melhorar a aprovação (antifraude MP).
 */
export function isValidCpf(value: string | null | undefined): boolean {
  const cpf = String(value ?? "").replace(/\D/g, "");
  if (cpf.length !== 11) return false;
  // Rejeita sequências repetidas (000.000.000-00, 111..., etc.)
  if (/^(\d)\1{10}$/.test(cpf)) return false;

  const digits = cpf.split("").map(Number);
  const calcCheckDigit = (length: number): number => {
    let sum = 0;
    for (let i = 0; i < length; i++) {
      sum += digits[i] * (length + 1 - i);
    }
    const remainder = (sum * 10) % 11;
    return remainder === 10 ? 0 : remainder;
  };

  return calcCheckDigit(9) === digits[9] && calcCheckDigit(10) === digits[10];
}

export function getEstimateBreakdown(pricePerPlayer: number, passFeesToUser: boolean = true) {
  if (pricePerPlayer <= 0) return null;
  const platformFeePix = getPlatformFee(pricePerPlayer, "PIX");
  // Para o cartão, consideramos apenas a parte da Lizta (10%) como taxa de plataforma;
  // a parte de 5% do cartão é sempre do usuário e não sai do time.
  const platformFeeCard = getPlatformFee(pricePerPlayer, "PIX");
  const chargedPix = getChargedAmount(pricePerPlayer, "PIX", passFeesToUser);
  const chargedCard = getChargedAmount(pricePerPlayer, "CARD", passFeesToUser);
  const netAfterMpPix = Math.round(chargedPix * (1 - MP_FEE_RATE_PIX) * 100) / 100;
  const netAfterMpCard = Math.round(chargedCard * (1 - MP_FEE_RATE_CARD) * 100) / 100;
  const mpFeePix = chargedPix * MP_FEE_RATE_PIX;
  const mpFeeCard = chargedCard * MP_FEE_RATE_CARD;
  const platformNetPix = Math.max(0, Math.round((platformFeePix - mpFeePix) * 100) / 100);
  const platformNetCard = Math.max(0, Math.round((platformFeeCard - mpFeeCard) * 100) / 100);
  return {
    chargedPix,
    chargedCard,
    platformFeePix,
    platformFeeCard,
    pix: {
      charged: chargedPix,
      netAfterMp: netAfterMpPix,
      platformNet: platformNetPix,
      teamReceives: passFeesToUser
        ? pricePerPlayer
        : Math.round(pricePerPlayer * (1 - PLATFORM_FEE_RATE_PIX) * 100) / 100,
    },
    card: {
      charged: chargedCard,
      netAfterMp: netAfterMpCard,
      platformNet: platformNetCard,
      teamReceives: passFeesToUser
        ? pricePerPlayer
        : Math.round(pricePerPlayer * (1 - PLATFORM_FEE_RATE_PIX) * 100) / 100,
    },
  };
}
